/**
 * Billing Service
 * Manages billing cycles and invoices for organizations
 */

import { supabase } from './supabase'

/**
 * Create a billing cycle
 */
export const createBillingCycle = async (organizationId, cycleData) => {
  try {
    const { data, error } = await supabase
      .from('billing_cycles')
      .insert({
        organization_id: organizationId,
        subscription_id: cycleData.subscriptionId,
        cycle_start_date: cycleData.startDate,
        cycle_end_date: cycleData.endDate,
        billing_cycle_type: cycleData.type || 'monthly',
        amount: cycleData.amount,
        currency: cycleData.currency || 'USD',
        due_date: cycleData.dueDate,
        status: 'pending',
      })
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error creating billing cycle:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get billing cycles for organization
 */
export const getBillingCycles = async (organizationId, filters = {}) => {
  try {
    let query = supabase
      .from('billing_cycles')
      .select('*, organization_subscriptions(tier_id, status)')
      .eq('organization_id', organizationId)
      .order('cycle_start_date', { ascending: false })

    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    if (filters.startDate) {
      query = query.gte('cycle_start_date', filters.startDate)
    }

    if (filters.endDate) {
      query = query.lte('cycle_end_date', filters.endDate)
    }

    const { data, error } = await query

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting billing cycles:', error)
    return { success: false, error: error.message, data: [] }
  }
}

/**
 * Update billing cycle status
 */
export const updateBillingCycleStatus = async (cycleId, organizationId, status) => {
  try {
    const updateData = {
      status,
      updated_at: new Date().toISOString(),
    }

    if (status === 'paid') {
      updateData.paid_date = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('billing_cycles')
      .update(updateData)
      .eq('id', cycleId)
      .eq('organization_id', organizationId)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error updating billing cycle:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get organization billing summary
 */
export const getBillingSummary = async (organizationId) => {
  try {
    const { data, error } = await supabase.rpc('get_organization_billing_summary', {
      org_id: organizationId,
    })

    if (error) throw error
    return { success: true, data: data?.[0] || null }
  } catch (error) {
    console.error('Error getting billing summary:', error)
    return { success: false, error: error.message }
  }
}

export default {
  createBillingCycle,
  getBillingCycles,
  updateBillingCycleStatus,
  getBillingSummary,
}

